/**
 * Every tunable number in the game lives here. Nothing else may hard-code a
 * threshold, weight, or multiplier — `scripts/retune.ts` sweeps this object
 * against logged sessions, so a constant buried in a component is invisible to
 * tuning and will silently rot.
 *
 * All times are SECONDS unless the name ends in `Ms`.
 */

export type Tier = "assisted" | "faded" | "blind";

export interface ScoringConfig {
  /** Onset matching: expected <-> detected association. */
  match: {
    /**
     * First-pass rejection window, in seconds. Deliberately generous: before
     * we've fit tempo/offset, a player who is internally consistent but 8%
     * fast drifts far outside a tight window by the end of a phrase, and a
     * tight first pass would throw away exactly the attempts we most want to
     * reward.
     */
    coarseWindow: number;
    /**
     * Second-pass window, applied in the warped domain (after a/b are known).
     * This is the one that decides what counts as a real hit.
     */
    fineWindow: number;
    /** How many match -> fit -> rematch iterations to run. 2 is plenty. */
    iterations: number;
    /**
     * Cost (in units of seconds of residual) charged for leaving an expected
     * onset unmatched, vs. for an unmatched detected onset. Extras are cheaper
     * than misses: breathing and consonant splits create spurious onsets that
     * aren't the player's fault, but skipping words is.
     */
    missCost: number;
    extraCost: number;
  };

  /** Least-squares fit of detected ≈ a * expected + b. */
  fit: {
    /**
     * Clamp on tempo scale `a`. A degenerate fit (few points, all clustered)
     * can produce an `a` that makes noise look consistent. Outside this range
     * we fall back to a = 1 and fit offset only, and flag the attempt.
     */
    minTempoScale: number;
    maxTempoScale: number;
    /** Below this many matched pairs, don't trust a two-parameter fit. */
    minPairsForTempoFit: number;
    /**
     * Drop the worst residuals before the final fit so one catastrophic
     * onset doesn't tilt the line. Fraction of pairs, 0 disables.
     */
    trimFraction: number;
    /**
     * Step size for the tempo-scale grid search that bootstraps the fit.
     * Finer costs time linearly; 0.0025 resolves tempo to a quarter of a
     * percent, well under what a player can perceive.
     */
    tempoGridStep: number;
    /**
     * Largest constant offset (s) the bootstrap will consider. Beyond this the
     * player didn't come in late, they came in on the wrong line.
     */
    maxOffset: number;
    /**
     * Histogram bin width (s) for the offset vote. Should be well under
     * match.fineWindow or the bootstrap resolves `b` too coarsely to help.
     */
    offsetBinWidth: number;
  };

  /**
   * Residual -> per-onset points. Score is a Gaussian falloff on the residual
   * (deviation from the player's OWN fitted line), not on raw deviation.
   */
  residual: {
    /** Residual (s) at which an onset scores ~0.61. Controls curve width. */
    sigma: number;
    /** Residual (s) beyond which an onset scores 0 outright. */
    cutoff: number;
  };

  /** Consecutive-onset streak multiplier. */
  streak: {
    /** Residual (s) under which an onset extends the streak. */
    threshold: number;
    /** Streak length at which the multiplier starts climbing. */
    minLength: number;
    /** Added to the multiplier per onset past minLength. */
    growth: number;
    /** Ceiling on the streak multiplier. */
    max: number;
  };

  /**
   * Penalties. Applied as a multiplicative factor on the timing quality, in
   * units of "one expected onset's worth" — `perMiss: 0.9` means each missed
   * word costs 0.9/expectedCount of the final score.
   *
   * They must NOT be mixed into the same denominator as the streak-multiplied
   * points: doing so lets a long streak inflate the denominator and quietly
   * dilute the penalty, so spraying 34 extra onsets over a section cost only
   * 12 points instead of 21.
   */
  penalty: {
    /** Fraction of one onset's worth removed per missed expected onset. */
    perMiss: number;
    /**
     * Extras are charged on PRECISION (matched / detected), not per onset.
     *
     * A linear per-extra cost cannot separate the two populations we care
     * about. Measured on the synthetic harness: a mumbler pulsing at 1.5x the
     * song's syllable rate sprays enough onsets to cover ~100% of the words,
     * and the linear penalty removes less than the coverage gains — so they
     * beat a genuine sloppy attempt. Raising the per-extra cost until that
     * stops costs a real singer (who breathes, and whose consonants
     * double-trigger the detector) about 11 points.
     *
     * Precision is the right shape because it scales with how much the player
     * sprayed RELATIVE to what they actually landed. 12% stray onsets is a
     * small deduction; doubling your onset count is a large one.
     *
     * Score is multiplied by precision ** precisionWeight.
     */
    precisionWeight: number;
    /**
     * Extras below this fraction of expected-onset count are forgiven
     * entirely — breath noise and consonant double-triggers.
     */
    extraGracePct: number;
    /**
     * Minimum fraction of expected onsets that must be matched for the attempt
     * to score at all. Below this the player didn't really sing the section and
     * a high residual score would be meaningless (this is the main structural
     * defence against the mumbling exploit).
     */
    minCoverage: number;
  };

  /** Difficulty tier multipliers. Applied last. */
  tierMultiplier: Record<Tier, number>;

  /** Faded tier: how long before the player reaches a line it disappears. */
  fadedLeadOut: number;

  /** Client-side capture + calibration. */
  capture: {
    /** Lead-in length range the chart picker aims for. */
    leadInMin: number;
    leadInMax: number;
    /**
     * Round-trip latency above this (s) is assumed to be a wireless output
     * path. Scoring is refused — A2DP adds 150-300ms and it drifts, so a
     * single calibration constant cannot correct it.
     */
    maxCalibratedLatency: number;
    /** Calibration is rejected if the click cross-correlation is weaker. */
    minCalibrationConfidence: number;
    /** Number of calibration clicks to average. */
    calibrationClicks: number;
  };

  /** Mic onset detection (spectral flux + VAD) running in the AudioWorklet. */
  onset: {
    /** Analysis hop, seconds. 512 frames @ 48k ≈ 10.7ms. */
    hopSize: number;
    fftSize: number;
    /** Flux must exceed (moving median * this) to fire. */
    fluxThresholdRatio: number;
    /** Absolute floor on flux, guards against firing in silence. */
    fluxFloor: number;
    /** Minimum gap between onsets (s). Below this is one syllable, not two. */
    minOnsetGap: number;
    /** RMS below this (dBFS) is treated as silence regardless of flux. */
    vadFloorDb: number;
    /** Frames of sustained energy required to open the VAD gate. */
    vadAttackFrames: number;
    /** Frames of silence required to close it. */
    vadReleaseFrames: number;
  };

  /** Chart generation quality gates (enforced in the Python worker). */
  chart: {
    /**
     * If aligner word timings disagree with the LRCLIB line start by more than
     * this (s), the chart is flagged rather than silently shipped. LRC is a
     * prior, not ground truth — but a large disagreement means the aligner
     * went off the rails, not that the LRC is slightly loose.
     */
    lrcAgreementTolerance: number;
    /** Fraction of lines allowed to breach the tolerance before flagging. */
    lrcAgreementMaxBreachPct: number;
    /** Words below this aligner confidence are excluded from scoring. */
    minWordConfidence: number;
  };
}

export const DEFAULT_CONFIG: ScoringConfig = {
  match: {
    coarseWindow: 0.9,
    fineWindow: 0.35,
    iterations: 2,
    missCost: 0.45,
    extraCost: 0.3,
  },
  fit: {
    minTempoScale: 0.75,
    maxTempoScale: 1.35,
    minPairsForTempoFit: 6,
    trimFraction: 0.1,
    tempoGridStep: 0.0025,
    maxOffset: 2.0,
    offsetBinWidth: 0.01,
  },
  residual: {
    sigma: 0.12,
    cutoff: 0.4,
  },
  streak: {
    threshold: 0.1,
    minLength: 4,
    growth: 0.04,
    max: 1.6,
  },
  penalty: {
    perMiss: 0.9,
    precisionWeight: 1.6,
    extraGracePct: 0.12,
    minCoverage: 0.5,
  },
  tierMultiplier: {
    assisted: 1.0,
    faded: 1.4,
    blind: 2.0,
  },
  fadedLeadOut: 1.2,
  capture: {
    leadInMin: 5,
    leadInMax: 10,
    maxCalibratedLatency: 0.14,
    minCalibrationConfidence: 0.35,
    calibrationClicks: 5,
  },
  onset: {
    hopSize: 512,
    fftSize: 2048,
    fluxThresholdRatio: 1.7,
    fluxFloor: 0.02,
    minOnsetGap: 0.08,
    vadFloorDb: -48,
    vadAttackFrames: 2,
    vadReleaseFrames: 12,
  },
  chart: {
    lrcAgreementTolerance: 0.5,
    lrcAgreementMaxBreachPct: 0.2,
    minWordConfidence: 0.25,
  },
};

/** Deep-merge a partial override onto the defaults. Used by the retune script. */
export function withOverrides(
  patch: DeepPartial<ScoringConfig>,
  base: ScoringConfig = DEFAULT_CONFIG,
): ScoringConfig {
  const out = structuredClone(base) as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = withOverrides(
        v as DeepPartial<ScoringConfig>,
        out[k] as ScoringConfig,
      );
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out as unknown as ScoringConfig;
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
